// SYSTEM_ROUTE: /work
import { useState } from 'react';
import type { ContentItem, ContentStage } from '@life-os/app-core';
import { CONTENT_STAGES, summarizeContent } from '@life-os/app-core';
import { Button, Chip, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const STAGE_TONE: Record<ContentStage, ChipTone> = {
  idea: 'muted',
  draft: 'neutral',
  review: 'warn',
  scheduled: 'warn',
  published: 'primary',
};

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

export function WorkScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [title, setTitle] = useState('');
  const [platform, setPlatform] = useState('');

  const items = useAsync<readonly ContentItem[]>(() => services.content.list(), [revision]);
  const list = items.value ?? [];
  const summary = summarizeContent(list);

  const add = async () => {
    if (title.trim().length === 0) return;
    await services.content.create({ title, platform });
    setTitle('');
    setPlatform('');
    refresh();
  };

  return (
    <>
      <ScreenHeader title={<>Work & Content</>} subtitle={<>Turn real work into public signal. Move pieces idea → draft → review → scheduled → published.
            Publishing is always your explicit call — no auto-posting.</>} />

      <Pane title="Pipeline" hint={`${summary.published} published · ${summary.inProgress} in progress`}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {CONTENT_STAGES.map((st) => (
            <Chip key={st} tone={summary.byStage[st] > 0 ? STAGE_TONE[st] : 'muted'}>
              {st} {summary.byStage[st] > 0 ? summary.byStage[st] : ''}
            </Chip>
          ))}
        </div>
      </Pane>

      <Pane title="New piece">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 220px' }}
            value={title}
            placeholder="Content idea / title…"
            onChange={(e) => setTitle(e.target.value)}
            aria-label="Content title"
          />
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 140px' }}
            value={platform}
            placeholder="Platform (e.g. blog)"
            onChange={(e) => setPlatform(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add();
            }}
            aria-label="Platform"
          />
          <Button variant="primary" onClick={() => void add()}>
            Add
          </Button>
        </div>
      </Pane>

      {CONTENT_STAGES.map((stage) => {
        const inStage = list.filter((c) => c.stage === stage);
        if (inStage.length === 0) return null;
        return (
          <Pane key={stage} title={stage[0]!.toUpperCase() + stage.slice(1)} hint={`${inStage.length}`}>
            <Stack>
              {inStage.map((c) => (
                <div key={String(c.id)} className="lc-row" style={{ alignItems: 'center', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <strong>{c.title}</strong>
                    {c.platform ? <span style={{ color: 'var(--lc-on-surface-variant)' }}> · {c.platform}</span> : null}
                  </div>
                  <Chip tone={STAGE_TONE[c.stage]}>{c.stage}</Chip>
                  <select
                    value={c.stage}
                    onChange={(e) => void services.content.setStage(c.id, e.target.value as ContentStage).then(refresh)}
                    aria-label="Stage"
                    style={inputStyle}
                  >
                    {CONTENT_STAGES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </Stack>
          </Pane>
        );
      })}

      {list.length === 0 ? (
        <Pane title="Content">
          <EmptyState title="No content yet">
            Capture a content idea sparked by a project or insight, then move it through to published.
          </EmptyState>
        </Pane>
      ) : null}
    </>
  );
}
